import React from 'react';
import { Dialog, DialogContent, Card, CardContent, Button, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import dayjs from 'dayjs';

const MarketDataDialog = ({
    selectedOrder,
    marketData,
    onClose,
    onOrderAction
}) => {
    if (!selectedOrder || !marketData) {
        return null;
    }
    const data = marketData[0];

    return (
        <Dialog
            open={selectedOrder !== null}
            onClose={onClose}
            sx={{
                position: 'fixed',
                left: 0,
                right: 2,
                bottom: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                boxSizing: 'border-box',
                top: 'auto',
            }}
            PaperProps={{
                sx: {
                    transition: 'transform 0.4s ease-in-out',
                    transform: selectedOrder !== null ? 'translateY(0)' : 'translateY(100vh)',
                    borderRadius: '20px 20px 0 0', // only top rounded
                    maxHeight: '90vh',
                    height: '100vh',
                    margin: '10px',
                    maxWidth: '400px',
                    bgcolor: 'gray.50',
                },
            }}
        >
            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: '1px' },
                    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent' },
                    p: 2,
                }}
            >
                <div className="w-full">

                    <div className="flex flex-wrap justify-between items-center">
                        <div className="flex items-center gap-3">
                            <span className="font-lato font-semibold text-black text-[12px]  ">
                                {data?.tradingSymbol || 'N/A'}
                            </span>
                            <span className="font-lato text-black  text-[12px] font-medium uppercase py-1 px-2 rounded-sm">
                                {data?.exchange || 'N/A'}
                            </span>
                        </div>
                        <span className="font-lato font-semibold text-[#089981] text-[13px]  ">
                            {data.ltp.toFixed(2) || 'N/A'}
                        </span>
                    </div>
                    {/* Additional Info */}
                    <div className="flex justify-between items-center">
                        <span className="text-[#f23645] text-[12px]">
                            Lots size: {data.lastTradeQty || 'N/A'}
                        </span>
                        <span className="text-gray-500 text-[12px]">
                            {data.netChange >= 0
                                ? `+${data.netChange || 0}`
                                : data.netChange || 0}
                            {` (${data.percentChange}%)`}
                        </span>
                    </div>
                    {/* OHLC Card */}
                    <Card className="rounded-md h-[60px] w-full mt-1">
                        <CardContent>
                            <div className="flex items-center justify-between -mt-[5px]">
                                {['Open', 'High', 'Low', 'Close'].map((label) => (
                                    <div key={label} className="flex flex-col items-center gap-1">
                                        <span className="font-lato text-sm text-gray-500">{label}</span>
                                        <span className="font-lato text-sm">
                                            {data?.[label.toLowerCase()] || 'N/A'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                    {/* Market Depth Table */}
                    <div className="mt-2 overflow-hidden">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell className="font-lato text-[14px] font-[400px] text-left p-1 border-none whitespace-nowrap leading-[17px]">QTY</TableCell>
                                    <TableCell className="font-lato text-[14px] font-normal text-right p-1 border-none whitespace-nowrap leading-[17px]">BUY PRICE</TableCell>
                                    <TableCell className="font-lato text-[14px] font-normal text-left p-1 border-none whitespace-nowrap leading-[17px]">SELL PRICE</TableCell>
                                    <TableCell className="font-lato text-[14px] font-normal text-right p-1 border-none whitespace-nowrap leading-[17px]">QTY</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody className="bg-white rounded-lg overflow-hidden">
                                {[...(data.depth?.buy || [])]
                                    .sort((a, b) => b.price - a.price)
                                    .map((buyOrder, index) => {
                                        const sellOrder = [...(data.depth?.sell || [])]
                                            .sort((a, b) => a.price - b.price)[index] || {};
                                        return (
                                            <TableRow key={index}>
                                                <TableCell className="font-lato text-left p-2 border-0 text-black text-[16px] font-normal leading-[19px] align-middle">
                                                    {buyOrder.orders || 'N/A'}
                                                </TableCell>
                                                <TableCell className="font-lato text-right p-2 border-0 text-[#089981] text-[16px] font-normal leading-[19px] align-middle">
                                                    {buyOrder.price || 'N/A'}
                                                </TableCell>
                                                <TableCell className="font-lato text-left p-2 border-0 text-[#f23645] text-[16px] font-normal leading-[19px] align-middle">
                                                    {sellOrder.price || 'N/A'}
                                                </TableCell>
                                                <TableCell className="font-lato text-right p-2 border-0 text-black text-[16px] font-normal leading-[19px] align-middle">
                                                    {sellOrder.orders || 'N/A'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}

                                {/* Totals row */}
                                <TableRow className="bg-white">
                                    <TableCell className="font-lato text-center p-1 pb-[7px] border-0 text-[16px] font-normal leading-[19px] align-middle">
                                        {data.depth?.buy?.reduce((sum, buyOrder) => sum + buyOrder.orders, 0)}
                                    </TableCell>
                                    <TableCell className="font-lato text-center p-1 border-0 text-black text-[16px] font-normal leading-[19px] align-middle" colSpan={2}>
                                        TOTAL AMOUNT
                                    </TableCell>
                                    <TableCell className="font-lato text-center p-1 pb-[7px] border-0 text-[16px] font-normal leading-[19px] align-middle">
                                        {data.depth?.sell?.reduce((sum, sellOrder) => sum + sellOrder.orders, 0)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </div>

                    {/* Volume and other details */}
                    <div className="mt-1">
                        {[
                            { label: 'Volume', value: data.tradeVolume },
                            { label: 'Last traded quantity', value: data.lastTradeQty },
                            { label: 'Last traded at', value: dayjs(data.exchTradeTime).format("YYYY-MM-DD HH:mm:ss") },
                            { label: 'Lower circuit', value: data.lowerCircuit },
                            { label: 'Upper circuit', value: data.upperCircuit }
                        ].map(({ label, value }) => (
                            <div key={label} className="flex justify-between items-center p-1">
                                <div className="flex items-center space-x-2">
                                    <span className="font-lato text-gray-600 text-[14px]">{label}</span>
                                </div>
                                <span className="font-lato text-[14px] text-black">{value}</span>
                            </div>
                        ))}
                    </div>

                    {/* Buy & Sell Buttons */}
                    <div className="mt-auto flex justify-center space-x-4 mb-[20px]">
                        {selectedOrder?.type === "sell" ? (
                            <Button
                                variant="outlined"
                                onClick={() => onOrderAction('buy', data.tradingSymbol, data.exchange, data.depth?.sell[0]?.price,
                                    selectedOrder.lots, data.netChange, data?.percentChange, data?.symbolToken,
                                    selectedOrder.script_id, selectedOrder.exchange_id, selectedOrder.order_id, selectedOrder.square_off,
                                    selectedOrder.qty, selectedOrder.expiry, selectedOrder.token, data.high, data.low, data.upperCircuit, data.lowerCircuit)}
                                className="font-lato min-w-[160px] rounded-[8px] bg-[#089981] text-white border-[#089981] py-[7px] px-[33px] text-[16px] font-semibold leading-[20px] flex flex-col items-center"
                            >
                                <span>BUY</span>
                                <div className="flex items-center space-x-1">
                                    <span className="font-lato text-[12px]">@ {data.depth?.sell[0]?.price}</span>
                                </div>
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                onClick={() => onOrderAction('sell', data.tradingSymbol, data.exchange, data.depth?.buy[0]?.price,
                                    selectedOrder.lots, data.netChange, data?.percentChange, data?.symbolToken,
                                    selectedOrder.script_id, selectedOrder.exchange_id, selectedOrder.order_id, selectedOrder.square_off,
                                    selectedOrder.qty, selectedOrder.expiry, selectedOrder.token, data.high, data.low, data.upperCircuit, data.lowerCircuit)}
                                className="font-lato min-w-[160px] rounded-[8px] bg-[#f23645] text-white border-[#f23645] py-[7px] px-[33px] text-[16px] font-semibold leading-[20px] flex flex-col items-center"
                            >
                                <span>SELL</span>
                                <div className="flex items-center space-x-1">
                                    <span className="font-lato text-[12px]">@ {data.depth?.buy[0]?.price}</span>
                                </div>
                            </Button>
                        )}
                    </div>

                </div>
            </DialogContent>
        </Dialog>
    );
};

export default MarketDataDialog;